import React, { useState, useEffect, useRef, createContext } from 'react'
import { Form, Dropdown } from 'react-bootstrap'
import Cookies from 'js-cookie'

export const SelectedItemContext = createContext(null)

const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || 'http://localhost:3000'
const AUTO_MODEL = { name: 'Auto (Runtime model config)', id: '' }
const DIRECT_PROVIDERS = new Set(['anthropic', 'openai', 'google'])

const normalizeModelId = modelId => {
  if (typeof modelId !== 'string') return ''

  const parts = modelId
    .trim()
    .split('/')
    .map(part => part.trim())
    .filter(Boolean)

  if (parts.length < 2) return ''

  const directProvider = parts[0].toLowerCase()
  if (DIRECT_PROVIDERS.has(directProvider)) {
    return `${directProvider}/${parts.slice(1).join('/')}`
  }

  const nestedProvider = parts[1]?.toLowerCase()
  if (parts.length >= 3 && DIRECT_PROVIDERS.has(nestedProvider)) {
    return `${nestedProvider}/${parts.slice(2).join('/')}`
  }

  return ''
}

const getInitialSelectedModel = () => {
  const sessionModelName = Cookies.get('session_model_name')
  if (!sessionModelName) return AUTO_MODEL

  try {
    const parsed = JSON.parse(sessionModelName)
    const normalizedId = normalizeModelId(parsed?.id)
    if (!normalizedId || parsed?.source !== 'manual') return AUTO_MODEL

    return {
      name: typeof parsed?.name === 'string' && parsed.name.trim() ? parsed.name : AUTO_MODEL.name,
      id: normalizedId,
      source: 'manual'
    }
  } catch {
    return AUTO_MODEL
  }
}

const SearchDropdown = () => {
  const initialSelected = getInitialSelectedModel()

  const [query, setQuery] = useState(initialSelected.name)
  const [items, setItems] = useState([])
  const [filteredItems, setFilteredItems] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedItem, setSelectedItem] = useState(initialSelected)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await fetch(`${NEXT_API_URL}/api/models`)
        const data = await res.json()
        if (Array.isArray(data.data)) {
          setItems(data.data)
        }
      } catch (err) {
        console.error('Error fetching model catalog:', err)
      }
    }
    fetchModels()
  }, [])

  useEffect(() => {
    if (query) {
      setFilteredItems(
        items.filter(item =>
          item.name.toLowerCase().includes(query.toLowerCase())
        )
      )
    } else {
      setFilteredItems(items)
    }
  }, [query, items])

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleItemClick = item => {
    Cookies.set('session_model_name', JSON.stringify({ name: item.name, id: item.id, source: 'manual' }))
    setQuery(item.name)
    setSelectedItem({ name: item.name, id: item.id, source: 'manual' })
    setShowDropdown(false)
  }

  const handleReset = () => {
    setQuery(AUTO_MODEL.name)
    setSelectedItem(AUTO_MODEL)
    Cookies.remove('session_model_name')
  }

  return (
    <SelectedItemContext.Provider value={selectedItem}>
      <div className='position-relative' ref={dropdownRef}>
        <div className='input-group'>
          <Form.Control
            type='text'
            placeholder='Search Models...'
            value={query}
            autoCapitalize='off'
            className='form-select'
            onChange={e => setQuery(e.target.value)}
            onFocus={() => {
              setShowDropdown(true)
              setFilteredItems(items)
            }}
          />
          <button
            type='button'
            className='btn btn-dark'
            style={{ marginTop: '5px' }}
            onClick={handleReset}
          >
            <i className='bi bi-arrow-clockwise'></i>
          </button>
        </div>
        {showDropdown && (
          <Dropdown.Menu
            show
            className='w-200'
            style={{
              maxHeight: '200px',
              overflowY: 'scroll',
              position: 'absolute',
              bottom: '100%',
              top: 'auto'
            }}
            data-bs-popper='static'
          >
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <Dropdown.Item
                  key={index}
                  onClick={() => handleItemClick(item)}
                  active={item.id === selectedItem.id}
                  ref={
                    item.id === selectedItem.id
                      ? el => {
                          if (el) {
                            el.scrollIntoView({
                              behavior: 'instant',
                              block: 'center'
                            })
                          }
                        }
                      : null
                  }
                >
                  {item.name}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled>No results found</Dropdown.Item>
            )}
          </Dropdown.Menu>
        )}
      </div>
    </SelectedItemContext.Provider>
  )
}

export default SearchDropdown
